import {
  FinancialEvent,
  FinancialLineItem,
  FinancialSnapshot,
  emptyFinancialSnapshot,
} from '@models/order';

export const PAYMENT_EVENT_TYPES = ['Deposit', 'Payment', 'Final Payment'];
export const REFUND_EVENT_TYPES = ['Refund'];
export const CREDIT_EVENT_TYPES = [
  'Applied Store Credit',
  'Frame Credit',
  'Lens Credit',
  'Frame + Lens Credit',
  'Return Credit',
  'Restyle Credit',
  'Custom Credit',
  'Gift Card Applied',
  'Tax Adjustment',
];

export class FinancialCalculator {
  static money(n: any): number {
    if (n == null || n === '') {
      return 0;
    }
    const raw = typeof n === 'string' ? n.replace(/,/g, '') : n;
    const v = parseFloat(raw);
    if (isNaN(v)) {
      return 0;
    }
    return Math.round(v * 100) / 100;
  }

  static isPolSelected(order: any): boolean {
    if (order?.pol) {
      return true;
    }
    const pol = order?.products?.[0]?.lens?.POL;
    if (pol === true) {
      return true;
    }
    if (typeof pol === 'string') {
      const trimmed = pol.trim().toLowerCase();
      return trimmed !== '' && trimmed !== 'false' && trimmed !== 'no';
    }
    return false;
  }

  static lensMarkedTaxable(order: any): boolean {
    const lens = order?.products?.[0]?.lens;
    if (!lens) {
      return false;
    }
    if (lens.taxable === true) {
      return true;
    }
    if (typeof lens.taxable === 'string') {
      const value = lens.taxable.trim().toLowerCase();
      return value === 'true' || value === 'yes';
    }
    return false;
  }

  static isLineTaxable(order: any, source: string): boolean {
    if (source === 'custom' || source === 'misc') {
      return true;
    }
    if (source === 'retail') {
      return !FinancialCalculator.isRxMerchandise(order);
    }
    return (
      !FinancialCalculator.isRxMerchandise(order) ||
      FinancialCalculator.lensMarkedTaxable(order)
    );
  }

  static isRxMerchandise(order: any): boolean {
    if (FinancialCalculator.isPolSelected(order)) {
      return true;
    }
    if (order?.frameOnly) {
      return false;
    }
    const type = String(order?.products?.[0]?.product?.type || order?.type || '').toUpperCase();
    if (type === 'RX' || type === 'O') {
      return true;
    }
    const lens = order?.products?.[0]?.lens;
    const productElement = order?.products?.[0] || {};
    if (
      lens &&
      (lens.material ||
        lens.type ||
        lens.lab ||
        FinancialCalculator.money(lens.price) > 0 ||
        FinancialCalculator.money(productElement.lensesPrice) > 0)
    ) {
      return true;
    }
    return false;
  }

  static buildLineItems(order: any): FinancialLineItem[] {
    const productElement = order?.products?.[0] || {};
    const lens = productElement.lens || {};
    const frame = productElement.frame || {};
    const items: FinancialLineItem[] = [];
    const frameAmt = FinancialCalculator.money(productElement.retail);
    if (frameAmt > 0) {
      items.push({
        category: 'FRAME',
        amount: frameAmt,
        taxable: FinancialCalculator.isLineTaxable(order, 'retail'),
        source: 'retail',
      });
    }

    const lensesAmt = FinancialCalculator.money(
      productElement.lensesPrice != null ? productElement.lensesPrice : lens.price
    );
    const tintAmt = FinancialCalculator.money(productElement.tintPrice);
    const coatingsAmt = FinancialCalculator.money(productElement.coatingsPrice);
    const customAmt = FinancialCalculator.money(productElement.custom);

    if (lensesAmt > 0) {
      items.push({
        category: 'LENSES',
        amount: lensesAmt,
        taxable: FinancialCalculator.isLineTaxable(order, 'lenses'),
        source: 'lenses',
      });
    }
    if (tintAmt > 0) {
      items.push({
        category: 'TINT',
        amount: tintAmt,
        taxable: FinancialCalculator.isLineTaxable(order, 'tint'),
        source: 'tint',
      });
    }
    if (coatingsAmt > 0) {
      items.push({
        category: 'COATINGS',
        amount: coatingsAmt,
        taxable: FinancialCalculator.isLineTaxable(order, 'coatings'),
        source: 'coatings',
      });
    }
    if (customAmt > 0) {
      items.push({
        category: 'CUSTOM',
        amount: customAmt,
        taxable: true,
        source: 'custom',
      });
    }

    let miscAmt = FinancialCalculator.money(productElement.miscPrice);
    miscAmt = FinancialCalculator.money(miscAmt + FinancialCalculator.money(frame.modificationPrice));
    miscAmt = FinancialCalculator.money(miscAmt + FinancialCalculator.money(lens.otherPrice));
    if (!(customAmt > 0)) {
      miscAmt = FinancialCalculator.money(miscAmt + FinancialCalculator.money(frame.partOtherPrice));
    }
    if (miscAmt > 0) {
      items.push({ category: 'MISC', amount: miscAmt, taxable: true, source: 'misc' });
    }
    return items;
  }

  static sumEvents(events: FinancialEvent[], types: string[]): number {
    return FinancialCalculator.money(
      (events || [])
        .filter((event) => types.indexOf(event.type) !== -1)
        .reduce((sum, event) => sum + FinancialCalculator.money(event.amount), 0)
    );
  }

  static calculate(
    order: any,
    options: {
      discount?: { type?: string; value?: number; eligibleCategories?: string[] };
      taxRate?: number;
      creditsApplied?: number;
      paid?: number;
    } = {}
  ): { lineItems: FinancialLineItem[]; financials: FinancialSnapshot } {
    const lineItems = FinancialCalculator.buildLineItems(order);
    const subtotal = FinancialCalculator.money(
      lineItems.reduce((sum, item) => sum + item.amount, 0)
    );
    const discounted = lineItems.map((item) => ({ ...item, discountedAmount: item.amount }));
    let discountAmount = 0;
    const discount = options.discount;
    if (discount && FinancialCalculator.money(discount.value)) {
      const eligibleCategories = discount.eligibleCategories || [];
      const eligible = discounted.filter((item) =>
        eligibleCategories.length ? eligibleCategories.indexOf(item.category) !== -1 : true
      );
      const eligibleTotal = FinancialCalculator.money(
        eligible.reduce((sum, item) => sum + item.amount, 0)
      );
      if (eligibleTotal > 0) {
        if (String(discount.type).toUpperCase() === 'FIXED') {
          discountAmount = FinancialCalculator.money(
            Math.min(FinancialCalculator.money(discount.value), eligibleTotal)
          );
          eligible.forEach((item) => {
            const share = (item.amount / eligibleTotal) * discountAmount;
            item.discountedAmount = FinancialCalculator.money(Math.max(0, item.amount - share));
          });
        } else {
          const pct = FinancialCalculator.money(discount.value) / 100;
          eligible.forEach((item) => {
            const cut = FinancialCalculator.money(item.amount * pct);
            discountAmount = FinancialCalculator.money(discountAmount + cut);
            item.discountedAmount = FinancialCalculator.money(Math.max(0, item.amount - cut));
          });
        }
      }
    }

    const taxRate = parseFloat(String(options.taxRate ?? 0));
    let tax = 0;
    if (!isNaN(taxRate) && taxRate > 0) {
      discounted.forEach((item) => {
        if (item.taxable) {
          tax = FinancialCalculator.money(tax + item.discountedAmount * (taxRate / 100));
        }
      });
    } else {
      tax = FinancialCalculator.money(order?.products?.[0]?.tax);
    }

    const postage = FinancialCalculator.money(order?.products?.[0]?.shipping);
    const orderTotal = FinancialCalculator.money(subtotal - discountAmount + tax + postage);
    const events: FinancialEvent[] = order?.financialEvents || [];
    const creditsApplied =
      options.creditsApplied != null
        ? FinancialCalculator.money(options.creditsApplied)
        : FinancialCalculator.sumEvents(events, CREDIT_EVENT_TYPES);
    const paid =
      options.paid != null
        ? FinancialCalculator.money(options.paid)
        : FinancialCalculator.money(
            FinancialCalculator.sumEvents(events, PAYMENT_EVENT_TYPES) -
              FinancialCalculator.sumEvents(events, REFUND_EVENT_TYPES)
          );
    const balanceDue = FinancialCalculator.money(Math.max(0, orderTotal - creditsApplied - paid));

    return {
      lineItems,
      financials: {
        subtotal,
        discount: discountAmount,
        tax,
        postage,
        orderTotal,
        creditsApplied,
        paid,
        balanceDue,
        suggestedDeposit: FinancialCalculator.money(balanceDue / 2),
      },
    };
  }

  static formatMoney(value: any): string {
    return FinancialCalculator.money(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  static empty(): FinancialSnapshot {
    return emptyFinancialSnapshot();
  }
}
